import React, { useState, useMemo, useCallback } from "react";
import { Drawer, CircularProgress } from "@mui/material";
import { useOperationService } from "../../../service/documentationVehicleService";
import {
  useVehicleOperations,
  useVehiclesByOperation,
} from "../../../hooks/useDocumentationVehicleQuery";
import { useLayout } from "../../../context/LayoutContext";
import { useNotification } from "../../../context/NotificationContext";
import { useQuery } from "@tanstack/react-query";
import VehicleDrawerHeader from "./vehicle/VehicleDrawerHeader";
import VehicleDrawerFilters from "./vehicle/VehicleDrawerFilters";
import VehiclesList from "./vehicle/VehiclesList";
import CombinedVehiclesList from "./vehicle/CombinedVehiclesList";
import AvailableVehicles from "./vehicle/AvailableVehicles";
import VehicleDetails from "./vehicle/VehicleDetails";
import DisableVehicleModal from "../modals/DisableVehicleModal";
// SE REMOVIO: import DeactivationMenu from "./vehicle/DeactivationMenu";xd
import "../../../assets/scss/components/ui/drawner/VehicleDrawer.scss";

const ITEMS_PER_PAGE = 20;

const VehicleDrawer = ({
  open,
  onClose,
  operationId,
  operationName,
  onVehiclesChange,
  selectedRouteId,
}) => {
  const { theme } = useLayout();
  const { showNotification } = useNotification();
  const operationService = useOperationService();

  const {
    assignVehicleToOperation,
    assignVehicleToOperationWithRoute,
    deactivateVehicleOperationRelation,
    removeVehicleFromRoute,
    isLoading: isVehicleOperationLoading,
    error: vehicleOperationError,
  } = useVehicleOperations();

  // Estados para la gestión de vehículos
  const [mode, setMode] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState(null);
  const [vehicleType, setVehicleType] = useState("all");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [page, setPage] = useState(1);
  const [isSearchOpen, setIsSearchOpen] = useState(false);


  // MANTENER SOLO EL ESTADO DEL MODAL:
  const [disableModal, setDisableModal] = useState({
    open: false,
    vehicleInfo: null,
  });

  // Hook para vehículos de operación (sin cambios)
  const {
    vehicles,
    combinedVehicles,
    pagination,
    totalPages,
    isLoading: isLoadingVehicles,
    error: vehiclesError,
    refetch: refetchVehicles,
  } = useVehiclesByOperation(operationId, {
    limit: ITEMS_PER_PAGE,
    page,
    search: searchTerm,
    includeSuboperations: false,
    includeInactive,
    vehicleType,
    operationRouteId: selectedRouteId,
    enabled: open && mode === "list" && !!operationId,
  });

  // Query para vehículos disponibles (sin cambios)
  const {
    data: availableVehiclesData,
    isLoading: isLoadingAvailable,
    error: availableError,
  } = useQuery({
    queryKey: [
      "availableVehiclesForOperation",
      operationId,
      {
        page,
        search: searchTerm,
        limit: ITEMS_PER_PAGE,
        includeInactive,
        vehicleType,
      },
    ],
    queryFn: () =>
      operationService.getAvailableVehiclesForOperation(operationId, {
        limit: ITEMS_PER_PAGE,
        page,
        search: searchTerm,
        includeInactive,
        vehicleType,
      }),
    enabled: open && mode === "add" && !!operationId,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Memoización y manejadores básicos (sin cambios hasta handleStatusChange)
  const availableVehicles = useMemo(
    () => availableVehiclesData?.data || [],
    [availableVehiclesData]
  );

  const availableCombinedVehicles = useMemo(
    () => availableVehiclesData?.combinedVehicles || [],
    [availableVehiclesData]
  );

  // FUNCIÓN HELPER PARA DETERMINAR SI UN VEHÍCULO ESTÁ ACTIVO
  const isVehicleActive = useCallback((vehicle) => {
    // Para vehículos normales
    if (vehicle.relation_active !== undefined) {
      return vehicle.relation_active && vehicle.vehicle_active;
    }
    
    // Para vehículos combinados
    if (vehicle.relation?.vehicle_relation_active !== undefined) {
      return vehicle.relation?.vehicle_relation_active && 
             vehicle.relation?.trailer_relation_active;
    }
    
    return false;
  }, []);

  // FUNCIÓN HELPER PARA OBTENER INFO DEL VEHÍCULO PARA EL MODAL
  const getVehicleInfoForModal = useCallback((vehicle) => {
    // Para vehículos normales
    if (vehicle.id_vehicle) {
      return {
        relationId: vehicle.id,
        vehicleId: vehicle.id_vehicle,
        plate: vehicle.plate,
        operationName: operationName,
        vehicleType: 'normal'
      };
    }
    
    // Para vehículos combinados
    if (vehicle.vehicle?.id_vehicle) {
      return {
        relationId: vehicle.relation?.id,
        vehicleId: vehicle.vehicle.id_vehicle,
        plate: `${vehicle.vehicle.plate} - ${vehicle.trailer?.plate}`,
        operationName: operationName,
        vehicleType: 'combined'
      };
    }
    
    return null;
  }, [operationName]);

  // MANEJADOR UNIFICADO PARA CAMBIO DE ESTADO (CORREGIDO)
  const handleStatusChange = useCallback(
    async (vehicle, event) => {
      console.log('🔧 handleStatusChange llamado:', { vehicle, isActive: isVehicleActive(vehicle) });
      
      // Verificar documentos vencidos antes de activar
      if (!isVehicleActive(vehicle) && vehicle.documentationBlocksActivation) {
        showNotification({
          message: "No se puede activar: documentos vencidos",
          severity: "error",
          details: vehicle.documentationDetails,
        });
        return;
      }

      if (isVehicleActive(vehicle)) {
        // VEHÍCULO ACTIVO -> ABRIR MODAL DE DESHABILITACIÓN
        const vehicleInfo = getVehicleInfoForModal(vehicle);
        
        if (!vehicleInfo) {
          console.error('❌ No se pudo obtener información del vehículo:', vehicle);
          showNotification({
            message: "Error: No se pudo obtener información del vehículo",
            severity: "error",
          });
          return;
        }
        
        console.log('✅ Abriendo modal con vehicleInfo:', vehicleInfo);
        
        setDisableModal({
          open: true,
          vehicleInfo,
        });
      } else {
        // VEHÍCULO INACTIVO -> ACTIVAR
        try {
          const vehicleId = vehicle.id_vehicle || vehicle.vehicle?.id_vehicle;
          
          if (!vehicleId) {
            throw new Error('No se pudo obtener el ID del vehículo');
          }
          
          const assignmentData = {
            vehicle_id: vehicleId,
            operation_id: operationId,
            is_active: true,
          };

          if (selectedRouteId) {
            await assignVehicleToOperationWithRoute({
              ...assignmentData,
              operationRouteId: selectedRouteId,
            });
          } else {
            await assignVehicleToOperation(assignmentData);
          }

          if (onVehiclesChange) onVehiclesChange();

          showNotification({
            message: "Vehículo activado correctamente",
            severity: "success",
          });
        } catch (error) {
          console.error("Error al activar vehículo:", error);
          showNotification({
            message: "Error al activar el vehículo",
            severity: "error",
          });
        }
      }
    },
    [
      isVehicleActive,
      getVehicleInfoForModal,
      operationId,
      selectedRouteId,
      assignVehicleToOperation,
      assignVehicleToOperationWithRoute,
      onVehiclesChange,
      showNotification,
    ]
  );

  // USAR EL MISMO MANEJADOR PARA VEHÍCULOS COMBINADOS
  const handleCombinedStatusChange = handleStatusChange;

  // REMOVER handleReasonSelect y handleMenuClose

  // MANEJADOR DE CONFIRMACIÓN DEL MODAL (CORREGIDO)
  const handleDisableConfirm = useCallback(
    async (disableData) => {
      console.log('🔧 handleDisableConfirm llamado:', disableData);
      
      try {
        await deactivateVehicleOperationRelation({
          relationId: disableData.relationId,
          disableData: {
            reason: disableData.reason,
            startDate: disableData.startDate,
            endDate: disableData.endDate,
            comment: disableData.comment,
          },
        });

        if (onVehiclesChange) onVehiclesChange();

        showNotification({
          message: "Vehículo deshabilitado correctamente",
          severity: "success",
        });

        setDisableModal({ open: false, vehicleInfo: null });
      } catch (error) {
        console.error("Error al deshabilitar vehículo:", error);
        showNotification({
          message: "Error al deshabilitar el vehículo",
          severity: "error",
        });
      }
    },
    [deactivateVehicleOperationRelation, onVehiclesChange, showNotification]
  );

  // Resto de manejadores (sin cambios)
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleAddMode = useCallback(() => {
    setMode("add");
    setPage(1);
    setSelectedVehicles([]);
    setSearchTerm("");
  }, []);

  const handleViewDetails = useCallback((vehicle) => {
    setSelectedVehicleDetails(vehicle);
    setMode("details");
  }, []);

  const handleBackToList = useCallback(() => {
    setMode("list");
    setPage(1);
    setSelectedVehicles([]);
    setSearchTerm("");
    setSelectedVehicleDetails(null);
  }, []);

  const handleSelectVehicle = useCallback((vehicleId) => {
    setSelectedVehicles((prev) => {
      if (prev.includes(vehicleId)) {
        return prev.filter((id) => id !== vehicleId);
      } else {
        return [...prev, vehicleId];
      }
    });
  }, []);

  const handleAssignVehicles = useCallback(async () => {
    if (selectedVehicles.length === 0) {
      showNotification({
        message: "Selecciona al menos un vehículo para asignar",
        severity: "warning",
      });
      return;
    }

    try {
      await Promise.all(
        selectedVehicles.map((vehicleId) => {
          if (selectedRouteId) {
            return assignVehicleToOperationWithRoute({
              vehicle_id: vehicleId,
              operation_id: operationId,
              operationRouteId: selectedRouteId,
              is_active: true,
            });
          } else {
            return assignVehicleToOperation({
              vehicle_id: vehicleId,
              operation_id: operationId,
              is_active: true,
            });
          }
        })
      );

      showNotification({
        message: `${selectedVehicles.length} vehículos asignados correctamente`,
        severity: "success",
      });

      if (onVehiclesChange) {
        onVehiclesChange();
      }

      handleBackToList();
    } catch (err) {
      console.error("Error al asignar vehículos:", err);
      showNotification({
        message: "Error al asignar vehículos",
        severity: "error",
      });
    }
  }, [
    selectedVehicles,
    assignVehicleToOperation,
    assignVehicleToOperationWithRoute,
    selectedRouteId,
    operationId,
    showNotification,
    onVehiclesChange,
    handleBackToList,
  ]);

  const handleSearchToggle = useCallback(() => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setTimeout(
        () => document.getElementById("vehicle-search-input")?.focus(),
        100
      );
    } else {
      setSearchTerm("");
    }
  }, [isSearchOpen]);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  }, []);

  const handleVehicleTypeChange = useCallback((e) => {
    setVehicleType(e.target.value);
    setPage(1);
  }, []);

  const handleIncludeInactiveChange = useCallback((e) => {
    setIncludeInactive(e.target.checked);
    setPage(1);
  }, []);

  const handleRemoveFromRoute = useCallback(
    async (relationId, vehiclePlate, routeName) => {
      try {
        await removeVehicleFromRoute({
          relationId,
          operationRouteId: selectedRouteId,
        });

        showNotification({
          message: `Vehículo ${vehiclePlate} eliminado de ${routeName}`,
          severity: "success",
        });

        if (onVehiclesChange) {
          onVehiclesChange();
        }
      } catch (error) {
        console.error("Error al eliminar vehículo de ruta:", error);
        showNotification({
          message: `Error al eliminar vehículo de la ruta: ${error.message}`,
          severity: "error",
        });
      }
    },
    [
      removeVehicleFromRoute,
      selectedRouteId,
      showNotification,
      onVehiclesChange,
    ]
  );

  // FUNCIÓN HELPER SIMPLIFICADA (CORREGIDA)
  const getInactiveReason = useCallback(
    (vehicle) => {
      if (isVehicleActive(vehicle)) return null;

      if (vehicle.documentationIssue) {
        return {
          type: "documentation",
          label:
            vehicle.documentationIssue === "expired"
              ? "Documentos Vencidos"
              : "Documentos Críticos",
          icon: "description",
          details:
            vehicle.documentationDetails ||
            "Documentos vencidos o próximos a vencer",
          blocksActivation: vehicle.documentationBlocksActivation,
        };
      }

      return {
        type: "unknown",
        label: vehicle.relation_comment || "Desactivado",
        icon: "warning",
        blocksActivation: false,
      };
    },
    [isVehicleActive]
  );

  const getDocumentStatus = useCallback((doc) => {
    if (!doc.expiration_date) return { type: "unknown", label: "N/A" };

    const today = new Date();
    const expDate = new Date(doc.expiration_date);
    const daysUntilExpiration = Math.ceil(
      (expDate - today) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiration < 0) {
      return { type: "expired", label: "Vencido" };
    } else if (daysUntilExpiration <= 15) {
      return { type: "critical", label: "Crítico" };
    } else if (daysUntilExpiration <= 30) {
      return { type: "warning", label: "Advertencia" };
    } else {
      return { type: "valid", label: "Válido" };
    }
  }, []);

  // Organización de vehículos (sin cambios)
  const sortedVehicles = useMemo(() => {
    const inactive = vehicles.filter(
      (v) => !v.relation_active || !v.vehicle_active
    );
    const active = vehicles.filter(
      (v) => v.relation_active && v.vehicle_active
    );
    return [...inactive, ...active];
  }, [vehicles]);

  const sortedCombinedVehicles = useMemo(() => {
    const inactive = combinedVehicles.filter(
      (v) =>
        !v.relation?.vehicle_relation_active ||
        !v.relation?.trailer_relation_active
    );
    const active = combinedVehicles.filter(
      (v) =>
        v.relation?.vehicle_relation_active &&
        v.relation?.trailer_relation_active
    );
    return [...inactive, ...active];
  }, [combinedVehicles]);

  const availableTotalPages = useMemo(() => {
    if (!availableVehiclesData?.pagination) return 1;
    return (
      Math.ceil(availableVehiclesData.pagination.total / ITEMS_PER_PAGE) || 1
    );
  }, [availableVehiclesData]);

  const isLoading =
    isLoadingVehicles || isLoadingAvailable || isVehicleOperationLoading;
  const error = vehiclesError || availableError || vehicleOperationError;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className={`GM__vehicle-drawer-container-${theme}`}
      PaperProps={{
        className: `GM__${theme}-vehicle-drawer-paper`,
      }}
    >
      <div className={`GM__${theme}-vehicle-drawer`}>
        <VehicleDrawerHeader
          theme={theme}
          mode={mode}
          operationName={operationName}
          selectedVehicleDetails={selectedVehicleDetails}
          onSearchToggle={handleSearchToggle}
          onAddMode={handleAddMode}
          onBackToList={handleBackToList}
          onClose={onClose}
          isSearchActive={isSearchOpen}
        />

        {mode !== "details" && (
          <VehicleDrawerFilters
            theme={theme}
            mode={mode}
            isSearchOpen={isSearchOpen}
            searchTerm={searchTerm}
            vehicleType={vehicleType}
            includeInactive={includeInactive}
            selectedVehiclesCount={selectedVehicles.length}
            isLoading={isLoading}
            onSearch={handleSearch}
            onVehicleTypeChange={handleVehicleTypeChange}
            onIncludeInactiveChange={handleIncludeInactiveChange}
            onAssignVehicles={handleAssignVehicles}
          />
        )}

        <div className={`GM__${theme}-vehicle-drawer-content ${mode}`}>
          {isLoading ? (
            <div className={`GM__${theme}-loading-container`}>
              <CircularProgress size={40} />
              <div className={`GM__${theme}-loading-message`}>
                Cargando vehículos...
              </div>
            </div>
          ) : error ? (
            <div className={`GM__${theme}-error-message`}>
              {error.message || "No se pudieron cargar los vehículos"}
            </div>
          ) : mode === "list" ? (
            <>
              {(vehicleType === "all" || vehicleType === "vehicles") && (
                <VehiclesList
                  theme={theme}
                  vehicles={sortedVehicles}
                  totalPages={totalPages}
                  currentPage={page}
                  onPageChange={handlePageChange}
                  onStatusChange={handleStatusChange}
                  onViewDetails={handleViewDetails}
                  onRemoveFromRoute={handleRemoveFromRoute}
                  getInactiveReason={getInactiveReason}
                  selectedRouteId={selectedRouteId}
                  operationName={operationName}
                />
              )}

              {(vehicleType === "all" || vehicleType === "combined") && (
                <CombinedVehiclesList
                  theme={theme}
                  combinedVehicles={sortedCombinedVehicles}
                  totalPages={totalPages}
                  currentPage={page}
                  onPageChange={handlePageChange}
                  onStatusChange={handleCombinedStatusChange}
                  onViewDetails={handleViewDetails}
                  onRemoveFromRoute={handleRemoveFromRoute}
                  getInactiveReason={getInactiveReason}
                  selectedRouteId={selectedRouteId}
                  operationName={operationName}
                />
              )}

              {((vehicleType === "all" &&
                sortedVehicles.length === 0 &&
                sortedCombinedVehicles.length === 0) ||
                (vehicleType === "vehicles" && sortedVehicles.length === 0) ||
                (vehicleType === "combined" &&
                  sortedCombinedVehicles.length === 0)) && (
                <div className={`GM__${theme}-empty-message`}>
                  {searchTerm
                    ? `No se encontraron vehículos para "${searchTerm}"`
                    : "No hay vehículos asignados a esta operación"}
                </div>
              )}
            </>
          ) : mode === "add" ? (
            <AvailableVehicles
              theme={theme}
              vehicleType={vehicleType}
              availableVehicles={availableVehicles}
              availableCombinedVehicles={availableCombinedVehicles}
              selectedVehicles={selectedVehicles}
              availableTotalPages={availableTotalPages}
              currentPage={page}
              searchTerm={searchTerm}
              onPageChange={handlePageChange}
              onSelectVehicle={handleSelectVehicle}
            />
          ) : (
            <VehicleDetails
              theme={theme}
              vehicle={selectedVehicleDetails}
              getDocumentStatus={getDocumentStatus}
            />
          )}
        </div>

        {/* REMOVER EL DeactivationMenu COMPLETAMENTE */}
        
        {/* MANTENER SOLO EL MODAL */}
        <DisableVehicleModal
          open={disableModal.open}
          onClose={() => setDisableModal({ open: false, vehicleInfo: null })}
          vehicleInfo={disableModal.vehicleInfo}
          onConfirm={handleDisableConfirm}
          theme={theme}
        />
      </div>
    </Drawer>
  );
};

export default VehicleDrawer;